"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { Crosshair, ArrowUpRight, Dices, Shield, RefreshCw } from "lucide-react";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { ClassIcon } from "@/components/icons/ClassIcon";
import { SpecIcon } from "@/components/icons/SpecIcon";
import { BattleNetIcon } from "@/components/icons/BattleNetIcon";
import { ProbabilityPill, probabilityColorVar } from "@/components/ProbabilityPill";
import { MelliBar } from "@/components/MelliBar";
import { EquipmentTable } from "@/components/EquipmentTable";
import { MarkRollDialog } from "@/components/MarkRollDialog";
import { PageHeader } from "../_components/PageHeader";
import { useLootCache } from "@/lib/loot-cache";
import {
  useCharState,
  setClass,
  applyEquipmentToMyth,
  setSelectedCharacter,
} from "@/lib/state";
import { useCharacters } from "@/lib/characters";
import {
  fetchEquipment,
  invalidateEquipment,
  type EquipmentResponse,
} from "@/lib/equipment";
import { computeRecommendations } from "@/lib/engine";
import { GROUP_BY_ID } from "@/lib/bis-slots";
import { classifyTrack } from "@/lib/myth";
import { CLASS_BY_ID, SPEC_BY_ID } from "@/lib/classes";
import type { GroupID } from "@/lib/types";
import { classColorVar, ROLE_LABEL } from "@/lib/wow";
import { useAuth, bnetLoginUrl } from "@/lib/auth";
import { cn } from "@/lib/utils";

export default function DashboardPage() {
  const state = useCharState();
  const cache = useLootCache();
  const characters = useCharacters();
  const auth = useAuth();
  const cls = CLASS_BY_ID[state.classID];
  const specEntry = SPEC_BY_ID[state.playedSpecID];

  const selected = useMemo(
    () => characters.list.find((c) => c.id === state.selectedCharacterID) ?? null,
    [characters.list, state.selectedCharacterID],
  );

  const [equipment, setEquipment] = useState<EquipmentResponse | null>(null);
  const [loadingEquipFor, setLoadingEquipFor] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [markRollOpen, setMarkRollOpen] = useState(false);

  // ── Auto-pick a character when the roster first loads ──────────────
  // The user shouldn't have to click a character on every visit. Prefer
  // a character matching the class they already configured on the
  // landing page (or last session) — surfacing a high-level alt of a
  // different class would mass-switch their classID and discard the
  // BiS work they just did. Fall back to the highest-level character
  // (top of the list) only when no class match exists.
  useEffect(() => {
    if (state.selectedCharacterID) return;
    if (characters.list.length === 0) return;
    const matching = characters.list.find((c) => c.classID === state.classID);
    const pick = matching ?? characters.list[0];
    setSelectedCharacter(pick.id);
    if (state.classID !== pick.classID) setClass(pick.classID);
    // setClass clears selectedCharacterID; restore it after the class
    // switch (only matters when no class match was found).
    setSelectedCharacter(pick.id);
  }, [characters.list, state.selectedCharacterID, state.classID]);

  // ── Auto-fetch equipment whenever selectedCharacterID changes ──────
  // Cached for 5 min so this is effectively free on revisits. The first
  // fetch after login takes a beat — that's why we show a spinner row.
  // Skip the fetch entirely for anonymous sessions (selectedCharacterID
  // can persist in localStorage across sign-outs and would otherwise
  // 401 the equipment endpoint on every dashboard mount).
  useEffect(() => {
    if (!state.selectedCharacterID || auth.state !== "signed-in") {
      setEquipment(null);
      return;
    }
    let cancelled = false;
    const id = state.selectedCharacterID;
    setLoadingEquipFor(id);
    void fetchEquipment(id)
      .then((eq) => {
        if (cancelled) return;
        if (eq) {
          setEquipment(eq);
          applyEquipmentToMyth(eq.items, cache);
        }
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingEquipFor((cur) => (cur === id ? null : cur));
      });
    return () => { cancelled = true; };
  }, [state.selectedCharacterID, cache, auth.state]);

  async function refreshEquipment() {
    if (!state.selectedCharacterID || refreshing) return;
    setRefreshing(true);
    invalidateEquipment(state.selectedCharacterID);
    try {
      const eq = await fetchEquipment(state.selectedCharacterID);
      if (eq) {
        setEquipment(eq);
        applyEquipmentToMyth(eq.items, cache);
      }
    } finally {
      setRefreshing(false);
    }
  }

  const rows = useMemo(
    () => computeRecommendations({ cache, state }),
    [cache, state],
  );
  const top = rows.slice(0, 3);

  const bis = state.bis[state.playedSpecID] ?? {};
  const filledSlots = Object.values(bis).filter((e) => e && e.mode !== "skip").length;
  const totalExcluded = Object.values(state.exclusions).reduce(
    (s, row) => s + Object.keys(row).length, 0,
  );
  const poolCount = Object.keys(cache.pools).length;
  // Two different counts the user might want:
  //   mythItemCount   — number of equipped pieces with the Myth badge
  //                     (matches what the gear panel visually shows)
  //   autoSkipCount   — number of catalyzable BiS groups where ALL
  //                     positions are at Myth, so the engine
  //                     auto-skips that slot in catalyzed BiS recs
  // These can differ: a single Myth trinket gives mythItemCount=1
  // but autoSkipCount=0 (TRINKET needs both positions for the
  // engine to skip), and TRINKET isn't catalyzable anyway.
  const mythItemCount = useMemo(() => {
    if (!equipment) return 0;
    const max = (() => {
      let m = 0;
      for (const it of equipment.items) if (it.ilvl > m && it.ilvl > 5) m = it.ilvl;
      return m;
    })();
    if (max <= 0) return 0;
    let count = 0;
    for (const it of equipment.items) {
      // Drop shirt/tabard placeholders and count only items the gear
      // panel renders as Myth.
      if (it.ilvl <= 5) continue;
      if (classifyTrack(it, max) === "myth") count += 1;
    }
    return count;
  }, [equipment]);
  const autoSkipCount = Object.keys(state.equippedMyth.satisfiedGroups).filter(
    (g) => GROUP_BY_ID[g as GroupID]?.catalyzable,
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          eyebrow="OVERVIEW"
          title="Dashboard"
          subtitle={cache.tier}
        />
        <Button
          variant="primary"
          size="sm"
          onClick={() => setMarkRollOpen(true)}
          className="shrink-0 mt-1"
        >
          <Dices className="h-4 w-4" />
          Mark roll
        </Button>
      </div>

      {/* Sign-in CTA — shown ONLY when the auth probe has resolved as
          anonymous. Hidden during the "loading" state to avoid a flash
          of "sign in" → "your characters" on every page nav. */}
      {auth.state === "anonymous" && (
        <Card className="border-accent-dim/40">
          <CardBody className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="font-display text-base text-fg leading-tight">
                Sign in to use your real character.
              </p>
              <p className="mt-1 text-xs text-fg-muted leading-relaxed">
                bonusroll.net pulls your live equipment from Battle.net so the
                engine knows which slots you already have at Myth-track. No
                sign-in = generic spec preview only.
              </p>
            </div>
            <a
              href={bnetLoginUrl(state.classID === 11 ? "eu" : "eu")}
              className={cn(
                "btn-battlenet-melli text-white",
                "inline-flex items-center justify-center gap-2 whitespace-nowrap",
                "h-10 px-4 text-sm rounded-sm font-medium select-none",
                "active:translate-y-px transition-transform",
                "shrink-0",
              )}
            >
              <BattleNetIcon size={18} />
              Continue with Battle.net
            </a>
          </CardBody>
        </Card>
      )}

      {/* Hero card — character-aware when selected, falls back to spec preview. */}
      {cls && specEntry && (
        <Card>
          <CardBody className="flex items-center gap-5">
            <ClassIcon classToken={cls.token} size={56} />
            <div className="flex-1 min-w-0">
              {selected ? (
                <>
                  <p
                    className="font-display text-2xl font-medium leading-tight truncate"
                    style={{ color: classColorVar(cls.token) }}
                  >
                    {selected.name}
                  </p>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <SpecIcon specID={specEntry.spec.id} size={18} />
                    <span className="text-sm text-fg-muted">
                      {specEntry.spec.name} {cls.name}
                    </span>
                    <span className="text-fg-subtle">|</span>
                    <span className="text-xs text-fg-subtle font-mono uppercase tracking-wider">
                      {selected.realmName} ({selected.region.toUpperCase()})
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <p
                    className="font-display text-2xl font-medium leading-tight"
                    style={{ color: classColorVar(cls.token) }}
                  >
                    {cls.name}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <SpecIcon specID={specEntry.spec.id} size={20} />
                    <span className="text-sm text-fg-muted">
                      {specEntry.spec.name} · {ROLE_LABEL[specEntry.spec.role]}
                    </span>
                  </div>
                </>
              )}
            </div>
            {selected && (
              <button
                type="button"
                onClick={refreshEquipment}
                disabled={refreshing || loadingEquipFor === selected.id}
                className={cn(
                  "inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider",
                  "text-fg-subtle hover:text-fg transition-colors disabled:opacity-50",
                )}
                title="Re-fetch live equipment from Battle.net"
              >
                <RefreshCw className={cn("h-3 w-3", refreshing && "animate-spin")} />
                Refresh gear
              </button>
            )}
          </CardBody>
        </Card>
      )}

      {/* Your gear — per-slot ilvl + track badge from live equipment.
          (The character roster lives in the topbar selector now —
          no duplication on the dashboard.) */}
      {selected && equipment && equipment.items.length > 0 && (
        <Card>
          <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-fg flex items-center gap-2">
                <Shield className="h-4 w-4 text-accent" />
                Your gear
              </h2>
              <p className="text-xs text-fg-muted mt-0.5">
                {mythItemCount > 0 ? (
                  <>
                    {mythItemCount} {mythItemCount === 1 ? "item" : "items"} at Myth-track
                    {autoSkipCount > 0 && (
                      <>
                        {" · "}
                        <span title="Catalyzable slots (head, shoulder, back, chest, wrist, hands, belt, legs, feet) where ALL positions are at Myth. Catalyzed BiS for these slots is auto-skipped from recommendations.">
                          {autoSkipCount} catalyzable {autoSkipCount === 1 ? "slot" : "slots"} auto-skipped
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  "Live equipment from Battle.net"
                )}
              </p>
            </div>
            <Link
              href="/bis"
              className="text-xs text-fg-muted hover:text-accent transition-colors inline-flex items-center gap-1"
            >
              Configure BiS <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <EquipmentTable equipped={equipment.items} cache={cache} />
        </Card>
      )}

      {/* Stat row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="BiS slots" value={`${filledSlots}/14`} link="/bis" />
        <StatTile label="Pools tracked" value={poolCount.toString()} link="/pools" />
        <StatTile label="Items excluded" value={totalExcluded.toString()} link="/pools" />
        <StatTile label="Top P%" value={top[0] ? `${(top[0].p * 100).toFixed(0)}%` : "—"} link="/recommendations" />
      </div>

      {/* Top recommendations */}
      <Card>
        <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-fg">Top recommendations</h2>
            <p className="text-xs text-fg-muted mt-0.5">Highest P(BiS-hit) for your spec</p>
          </div>
          <Link
            href="/recommendations"
            className="text-xs text-fg-muted hover:text-accent transition-colors inline-flex items-center gap-1"
          >
            View all <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        {top.length === 0 ? (
          <CardBody className="py-10 text-center">
            <p className="text-sm text-fg-muted">
              No targets yet —{" "}
              <Link href="/bis" className="text-accent hover:underline">
                configure BiS
              </Link>
              {" "}to see recommendations.
            </p>
          </CardBody>
        ) : (
          <ul className="divide-y divide-border">
            {top.map((r, i) => (
              <li key={r.pool.id}>
                <Link
                  href="/recommendations"
                  className="px-5 py-3 flex items-center gap-4 hover:bg-surface-2/40 transition-colors"
                >
                  <span className="font-mono tabular text-xs text-fg-subtle w-6 text-center">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-fg truncate">{r.pool.label}</p>
                    <p className="text-[11px] text-fg-subtle font-mono uppercase tracking-wider mt-0.5">
                      {r.pool.isRaid ? "Raid" : "M+ Dungeon"} · loot spec {r.specName}
                      {r.specID !== state.playedSpecID && " ⇢ switch"}
                    </p>
                  </div>
                  <div className="hidden sm:block w-[100px]">
                    <MelliBar value={r.p} color={probabilityColorVar(r.p)} size="sm" />
                  </div>
                  <ProbabilityPill p={r.p} hits={r.hits} poolSize={r.poolSize} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <QuickAction
          href="/bis"
          Icon={Crosshair}
          title="Configure BiS"
          body={`${filledSlots} of 14 slots set. Mark catalyzable, specific, or skip.`}
        />
        <QuickActionButton
          Icon={Dices}
          title="Mark a roll"
          body={`${totalExcluded} items rolled so far. One click — they stop counting toward P% from then on.`}
          onClick={() => setMarkRollOpen(true)}
        />
      </div>

      <MarkRollDialog open={markRollOpen} onClose={() => setMarkRollOpen(false)} />
    </div>
  );
}

function StatTile({
  label,
  value,
  link,
}: {
  label: string;
  value: string;
  link?: string;
}) {
  const inner = (
    <div className="rounded-md border border-border bg-surface p-4 transition-colors hover:border-border-strong">
      <p className="eyebrow">{label}</p>
      <p className="mt-2 font-display text-2xl font-medium tabular text-fg">
        {value}
      </p>
    </div>
  );
  if (link) return <Link href={link}>{inner}</Link>;
  return inner;
}

function QuickAction({
  href,
  Icon,
  title,
  body,
}: {
  href: string;
  Icon: typeof Crosshair;
  title: string;
  body: string;
}) {
  return (
    <Link
      href={href}
      className="rounded-md border border-border bg-surface p-5 transition-colors hover:border-accent-dim hover:bg-surface-2/40 group"
    >
      <Icon className="h-4 w-4 text-accent" />
      <h3 className="mt-2 font-display text-base text-fg">{title}</h3>
      <p className="mt-1 text-xs text-fg-muted leading-relaxed">{body}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-xs text-fg-subtle group-hover:text-accent transition-colors">
        Open <ArrowUpRight className="h-3 w-3" />
      </span>
    </Link>
  );
}

function QuickActionButton({
  Icon,
  title,
  body,
  onClick,
}: {
  Icon: typeof Crosshair;
  title: string;
  body: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-border bg-surface p-5 text-left transition-colors hover:border-accent-dim hover:bg-surface-2/40 group"
    >
      <Icon className="h-4 w-4 text-accent" />
      <h3 className="mt-2 font-display text-base text-fg">{title}</h3>
      <p className="mt-1 text-xs text-fg-muted leading-relaxed">{body}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-xs text-fg-subtle group-hover:text-accent transition-colors">
        Open <ArrowUpRight className="h-3 w-3" />
      </span>
    </button>
  );
}
